/**
 * The Finance board as a Kanban (Isaiah Cotto, 2026-10-13): the operational kit (components/board/kanban) over
 * GET /finance/kanban (services/finance/financeKanban.js). One card is ONE HANDOFF.
 *
 * The server places every card: seven columns over the board's eleven handoff states, each with its exact count and
 * a page of every state it holds, counted in one statement (F3, 2026-10-14: no longer eleven /board reads). "Show
 * more" reads that column's next page. Held approvals and legacy Monday rows are Elsewhere counts, never columns.
 *
 * A drop never writes (FINANCE_TRANSITIONS): an allowed move opens the card's docked panel (CardActPanel) with the
 * governed act's own drawer, against the card's own handoff; anything else is refused in one line. Without
 * manage_handoff the board is read-only: no drag, and "Move…" offers nothing.
 *
 * A drop's request lives exactly until the panel takes it (F1): it is handed to the card it was made for, consumed
 * once, and dropped when the dock closes or another record opens -- so reopening a card, Previous / Next or the
 * browser's back never opens an act by itself.
 *
 * The Kanban is the default Board layout since 2026-10-14 (FinanceShell; ?layout=table is the table). Nothing here colours or orders by time.
 *
 * Mark ATD (2026-10-15): every card says its resident's ATD word, and "Move…" offers "Mark ATD…" (an in-place act,
 * TransitionMap.inPlace) to a viewer who may mark ATD. It opens the card's dock at the ATD panel's confirmation for the
 * card's own resident; after the mark, the board is read again and its status line says the result. Finance's columns
 * are handoff states, so the card stays in its column: there is no ATD column to move to.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OperationalKanban, LayoutSwitch, CardChips, CardStatus } from '../../components/board/kanban';
import type { KanbanColumnData, ElsewhereBucket } from '../../components/board/kanban';
import { useBoardUrlState } from '../../components/board';
import { outcomeOf, money, FinanceScope, PRIVATE_PAY_LABEL, payerLine } from './financeApi';
import { financeKanbanApi, FinanceKanbanPage, FinanceFilterOption } from './boardApi';
import { easternDay } from './HandoffWork';
import CardActPanel from './CardActPanel';
import type { CardActRequest } from './CardActPanel';
import { FINANCE_TRANSITIONS, STATE_WORD, cardFromRow, chipsFor, returnedWord, atdCardWords } from './financeKanbanModel';
import type { FinanceCard, FinanceColumnKey, FinanceKanbanCtx, FinanceBoardAct } from './financeKanbanModel';
import BuildingOrClinic from '../../components/emerge/BuildingOrClinic';

/** Cards per state per page (the server caps it at 100). */
export const KANBAN_PAGE_SIZE = 25;

const centsMoney = (c: number | null | undefined) => (c === null || c === undefined ? null : money(c / 100));
/** The handed day as Florida reads it, without the year inside the current year (the kit's K6), so it never crowds the line. */
export function handedDay(ts: string, now: Date = new Date()): string {
  const day = easternDay(ts);
  const year = now.toLocaleDateString('en-US', { timeZone: 'America/New_York', year: 'numeric' });
  return day.endsWith(`, ${year}`) ? day.slice(0, -(`, ${year}`).length) : day;
}
const NotRecorded = ({ children = 'not recorded' }: { children?: React.ReactNode }) => <span className="fn-kc-none">{children}</span>;

/**
 * What a Finance card says. Phrasing content only: the card's body is the button that opens it. Each row is one line
 * (the kit's rows never wrap): the label on the left gives way, the value on the right never does. Null money is
 * "not recorded", never $0.
 */
export function FinanceCardBody({ card }: { card: FinanceCard }) {
  const r = card.row;
  const h = r.handoff;
  const word = card.state === 'returned' ? returnedWord(card) : STATE_WORD[card.state];
  const kind = card.kind === 'PRIVATE_PAY' ? (h?.label || PRIVATE_PAY_LABEL) : 'UMED';
  const sold = card.kind === 'PRIVATE_PAY' ? centsMoney(h?.sold ? h.sold.total_cents : null) : (r.sale && r.sale.amount !== null ? money(r.sale.amount) : null);
  const terms = h?.terms ? centsMoney(h.terms.total_cents) : null;
  const acceptor = h?.accepted ? (h.accepted.by?.name || 'Name not recorded') : null;
  return (
    <>
      <span className="okb-c-name">{card.name}</span>
      <span className="okb-c-sub" title={r.facility?.name || undefined}>{r.facility ? (r.facility.name || 'Building name not recorded') : <BuildingOrClinic row={{ counselear_clinic_id: r.counselear_clinic?.id ?? null, counselear_clinic_name: r.counselear_clinic?.name ?? null }} fallback="No current building" />}</span>
      <span className="okb-c-row">
        <CardStatus word={word} color={r.emerge_state ? r.emerge_state.color : null} emptyLabel="No state" after={kind} />
      </span>
      <span className="okb-c-row fn-kc-line">
        <span className="okb-c-next">Sold</span>{' '}
        <span className="okb-c-due">{sold || <NotRecorded />}</span>
      </span>
      {card.kind === 'PRIVATE_PAY' && (
        <span className="okb-c-row fn-kc-line">
          <span className="okb-c-next" title={payerLine(h?.sold?.payer)}>{payerLine(h?.sold?.payer)}</span>
        </span>
      )}
      <span className="okb-c-row fn-kc-line">
        <span className="okb-c-next">Terms</span>{' '}
        <span className="okb-c-due">{terms || <NotRecorded />}</span>
      </span>
      <span className="okb-c-row fn-kc-line">
        <span className="okb-c-next">{h?.invoice ? 'Invoice recorded' : <NotRecorded>No invoice</NotRecorded>}</span>{' '}
        {h?.handed_at && <span className="okb-c-due">Handed {handedDay(h.handed_at)}</span>}
      </span>
      {/* ATD (2026-10-15): the resident's word, so a card marked from here says so; Finance has no ATD column. */}
      <span className="okb-c-row fn-kc-line">
        <span className="okb-c-next">ATD</span>{' '}
        <span className="okb-c-due">{atdCardWords(r) || <NotRecorded>none recorded</NotRecorded>}</span>
      </span>
      <CardChips chips={chipsFor(r)} />
      <span className="okb-c-foot">
        <span className={`okb-c-owner${acceptor ? '' : ' okb-c-owner--none'}`}><span className="okb-sr">Accepted by: </span>{acceptor || 'Not accepted yet'}</span>
      </span>
    </>
  );
}

/** The open handoff at a glance, under its name in the dock (2026-10-30): the card's building, state and acceptor. */
export function FinanceDockMeta({ card }: { card: FinanceCard }) {
  const r = card.row;
  const h = r.handoff;
  const word = card.state === 'returned' ? returnedWord(card) : STATE_WORD[card.state];
  const kind = card.kind === 'PRIVATE_PAY' ? (h?.label || PRIVATE_PAY_LABEL) : 'UMED';
  const acceptor = h?.accepted ? (h.accepted.by?.name || 'Name not recorded') : null;
  return (
    <>
      <span className="board-dock__metaitem">{r.facility ? (r.facility.name || 'Building name not recorded') : <BuildingOrClinic row={{ counselear_clinic_id: r.counselear_clinic?.id ?? null, counselear_clinic_name: r.counselear_clinic?.name ?? null }} fallback="No current building" />}</span>
      <CardStatus word={word} color={r.emerge_state ? r.emerge_state.color : null} emptyLabel="No state" after={kind} />
      <span className={`board-dock__metaitem okb-c-owner${acceptor ? '' : ' okb-c-owner--none'}`}><span className="okb-sr">Accepted by: </span>{acceptor || 'Not accepted yet'}</span>
    </>
  );
}

type Columns = Array<KanbanColumnData<FinanceCard> & { page: number }>;

const optionsOf = (all: string, xs: FinanceFilterOption[] | undefined) => [
  { value: '', label: all },
  ...(xs || []).map((o) => ({ value: o.id, label: `${o.name} (${o.count.toLocaleString('en-US')})` })),
];
const cardsOf = (rows: FinanceKanbanPage['columns'][number]['cards']) => rows.map(cardFromRow).filter((c): c is FinanceCard => Boolean(c));

export interface FinanceKanbanProps {
  scope: FinanceScope;
  /** The toolbar's Kanban | Table switch. Omitted (2026-10-14): the workspace sidebar's Views group chooses the layout. */
  onLayout?: (layout: 'kanban' | 'table') => void;
  /** An Elsewhere bucket: open its tab in the table. */
  onElsewhere: (tab: string) => void;
  /** The ATD command is open to this viewer (the shell's one GET /atd/meta): cards offer "Mark ATD…" (2026-10-15). */
  canMarkAtd?: boolean;
}

export default function FinanceKanban({ scope, onLayout, onElsewhere, canMarkAtd = false }: FinanceKanbanProps) {
  const [st, update] = useBoardUrlState(['acceptor', 'building', 'atd'], 'open');
  const [columns, setColumns] = useState<Columns>([]);
  const [head, setHead] = useState<FinanceKanbanPage | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [request, setRequest] = useState<CardActRequest | null>(null);
  const asks = useRef(0);
  const inFlight = useRef<AbortController | null>(null);
  /** Each whole-board read is a generation; a "Show more" begun in an older one is cancelled and its answer dropped (N3). */
  const generation = useRef(0);
  const moreInFlight = useRef(new Map<string, AbortController>());
  /** Every card seen this session, so an open card that an act moved off the loaded pages still has a dock. */
  const known = useRef(new Map<string, FinanceCard>());
  const remember = (cards: FinanceCard[]) => { for (const c of cards) known.current.set(c.id, c); };

  const query = useMemo(() => ({ q: st.q || undefined, acceptor: st.filters.acceptor || undefined, building: st.filters.building || undefined, atd: st.filters.atd || undefined }),
    [st.q, st.filters.acceptor, st.filters.building, st.filters.atd]);

  /** The whole board: one read. A failure is said in place of the board, never as empty columns. */
  const load = useCallback(async () => {
    if (inFlight.current) inFlight.current.abort();
    generation.current += 1;
    moreInFlight.current.forEach((c) => c.abort());
    moreInFlight.current.clear();
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    inFlight.current = ctl;
    const stale = () => inFlight.current !== ctl;
    setLoading(true); setErr(null);
    try {
      const r = await financeKanbanApi.read({ ...query, limit: KANBAN_PAGE_SIZE }, ctl ? ctl.signal : undefined);
      if (stale()) return;
      if (!r.success || !r.data) { setErr(outcomeOf(r)); setColumns([]); setHead(null); return; }
      const cols = r.data.columns.map((c) => {
        const cards = cardsOf(c.cards);
        remember(cards);
        return { key: c.key, label: c.label, what: c.what, count: c.count, has_more: c.has_more, page: c.page, cards };
      });
      setColumns(cols); setHead(r.data);
    } catch {
      if (!stale()) { setErr('Could not reach the server, so nothing here is current.'); setColumns([]); setHead(null); }
    } finally {
      if (!stale()) setLoading(false);
    }
  }, [query]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => () => { if (inFlight.current) inFlight.current.abort(); }, []);

  /** "Show more": the column's next page (the next page of each of its states). */
  const loadMore = useCallback(async (key: string) => {
    const col = columns.find((c) => c.key === key);
    if (!col || col.loading_more) return;
    const gen = generation.current;
    const ctl = typeof AbortController === 'function' ? new AbortController() : null;
    if (ctl) moreInFlight.current.set(key, ctl);
    const superseded = () => generation.current !== gen || (ctl !== null && ctl.signal.aborted);
    setColumns((cs) => cs.map((c) => (c.key === key ? { ...c, loading_more: true } : c)));
    try {
      const r = await financeKanbanApi.read({ ...query, column: key, page: col.page + 1, limit: KANBAN_PAGE_SIZE }, ctl ? ctl.signal : undefined);
      // The board was read again meanwhile: this page belongs to a board that is gone. Drop it.
      if (superseded()) return;
      const got = r.success && r.data ? r.data.columns[0] : null;
      setColumns((cs) => cs.map((c) => {
        if (c.key !== key) return c;
        if (!got) return { ...c, loading_more: false };
        const have = new Set(c.cards.map((k) => k.id));
        const more = cardsOf(got.cards).filter((k) => !have.has(k.id));
        remember(more);
        return { ...c, loading_more: false, count: got.count, has_more: got.has_more, page: got.page, cards: [...c.cards, ...more] };
      }));
      if (!got) setNotice(`The next cards could not be read. ${outcomeOf(r)}`);
    } catch {
      if (superseded()) return;
      setColumns((cs) => cs.map((c) => (c.key === key ? { ...c, loading_more: false } : c)));
      setNotice('The next cards could not be read: could not reach the server.');
    } finally {
      if (ctl && moreInFlight.current.get(key) === ctl) moreInFlight.current.delete(key);
    }
  }, [columns, query]);

  /* Mark ATD (2026-10-15): offered on a card only when the ATD command is open to this viewer (the shell's GET /atd/meta). */
  const ctx: FinanceKanbanCtx = useMemo(() => ({ canManage: Boolean(scope.can.manage_handoff), canSendBack: Boolean(scope.can.send_back), canMarkAtd }), [scope, canMarkAtd]);

  /* ── A drop's request: made for one card, consumed once, dropped when the dock leaves that card (F1) ── */
  const onAct = useCallback((card: FinanceCard, act: string) => {
    asks.current += 1;
    setRequest({ cardId: card.id, act: act as FinanceBoardAct, n: asks.current });
    update({ open: card.id });
  }, [update]);
  const onConsumed = useCallback((n: number) => setRequest((r) => (r && r.n === n ? null : r)), []);
  useEffect(() => {
    // The dock closed, or moved to another record (Previous / Next, a click, the browser's back): the request is void.
    setRequest((r) => (r && r.cardId !== st.open ? null : r));
  }, [st.open]);

  const onChanged = useCallback((msg: string) => { if (msg) setNotice(msg); load(); }, [load]);

  const buckets: ElsewhereBucket[] = (head?.elsewhere || []).map((e) => ({
    // The table's tab, named as the table (the Kanban is the default layout since 2026-10-14; F5).
    key: e.key, label: e.label, count: e.count, what: e.what, href: `/portal/finance?layout=table&tab=${e.tab}`, onSelect: () => onElsewhere(e.tab),
  }));
  const main = head && typeof head.total === 'number' ? head.total : null;
  const nf = new Intl.NumberFormat('en-US');
  const options = head ? head.filter_options : null;

  return (
    <OperationalKanban<FinanceCard, FinanceKanbanCtx>
      label="Finance board"
      idBase="fn-kanban"
      unit={['handoff', 'handoffs']}
      columns={columns}
      renderCard={(card) => <FinanceCardBody card={card} />}
      cardName={(card) => card.name}
      transitions={FINANCE_TRANSITIONS}
      ctx={ctx}
      onAct={onAct}
      onLoadMore={loadMore}
      loading={loading}
      error={err}
      onRetry={load}
      notice={notice}
      dragDisabled={!ctx.canManage}
      emptyText={st.q ? `No handoff here matches “${st.q}”.` : 'Nothing here.'}
      toolbar={{
        search: { value: st.q, onChange: (q) => update({ q }), placeholder: 'Resident or building', label: 'Search the Finance board by resident or building' },
        filters: [
          { key: 'acceptor', label: 'Accepted by', value: st.filters.acceptor, options: optionsOf('Anyone', options?.acceptors), onChange: (v) => update({ filters: { acceptor: v } }) },
          { key: 'building', label: 'Building', value: st.filters.building, options: optionsOf('Every building', options?.buildings), onChange: (v) => update({ filters: { building: v } }) },
        ],
        summary: main !== null ? <>{nf.format(main)} {main === 1 ? 'handoff' : 'handoffs'} on the board{ctx.canManage ? '' : ' · read-only'}</> : null,
        viewSwitch: onLayout ? <LayoutSwitch value="kanban" onChange={onLayout} /> : undefined,
      }}
      elsewhere={{ label: 'Not on this board', buckets, note: 'Worked in the table.' }}
      detail={{
        openId: st.open,
        onOpen: (id) => update({ open: id }),
        onClose: () => update({ open: null }),
        memoryKey: 'finance-kanban',
        label: (card) => (card ? `${card.name}: this handoff` : 'The open handoff'),
        title: (card) => {
          const c = card || (st.open ? known.current.get(st.open) : undefined);
          return <strong>{c ? c.name : 'Handoff'}</strong>;
        },
        meta: (card) => {
          const c = card || (st.open ? known.current.get(st.open) : undefined);
          return c ? <FinanceDockMeta card={c} /> : null;
        },
        render: (card) => {
          const c = card || (st.open ? known.current.get(st.open) || null : null);
          if (!c) return <section className="um-card"><p className="um-quiet">This handoff is not among the cards loaded. Find it with the search, or open it from the table.</p></section>;
          return <CardActPanel key={c.id} card={c} scope={scope} request={request && request.cardId === c.id ? request : null} onConsumed={onConsumed} onChanged={onChanged} />;
        },
      }}
    />
  );
}

export type { FinanceColumnKey };
